import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  Badge,
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Divider,
  TextField
} from '@mui/material';
import {
  Inbox as InboxIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  MoreVert as MoreVertIcon,
  Circle as CircleIcon,
  FiberManualRecord as OnlineDotIcon,
  CheckCircle as VerifiedIcon,
  NotificationsOff as MuteIcon,
  NotificationsActive as UnmuteIcon,
  Block as BlockIcon,
  Flag as ReportIcon,
  Delete as DeleteIcon,
  MarkEmailUnread as UnreadIcon,
  Drafts as ReadIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import { useSocket } from '../../contexts/SocketContext';
import { format, formatDistanceToNow } from 'date-fns';
import { getFullImageUrl } from '../../utils/helpers';

interface Conversation {
  partner: {
    id: string;
    firstName: string;
    lastName: string;
    preferredName?: string;
    profilePicture?: string;
    driverRating?: number;
    passengerRating?: number;
    isDriver: boolean;
  };
  latestMessage: {
    id: string;
    content: string;
    senderId: string;
    createdAt: string;
  };
  unreadCount: number;
  contextType?: 'ride' | 'request' | 'support';
  contextId?: string;
  contextDetails?: {
    originCity?: { name: string; province: string };
    destinationCity?: { name: string; province: string };
    departureDateTime?: string;
    preferredDateTime?: string;
    status?: string;
    subject?: string;
  };
  userRole?: 'driver' | 'passenger' | 'reporter';
  isArchived: boolean;
  isMuted: boolean;
  isBlocked: boolean;
}

const InboxPage: React.FC = () => {
  const navigate = useNavigate();
  const { isOnline, onMessageReceive, onMessageStatusUpdate } = useSocket();
  
  const [activeTab, setActiveTab] = useState<'inbox' | 'archived'>('inbox');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Kebab menu state
  const [menuAnchor, setMenuAnchor] = useState<{ element: HTMLElement; conversation: Conversation } | null>(null);
  const [confirmArchive, setConfirmArchive] = useState<Conversation | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Conversation | null>(null);
  const [confirmBlock, setConfirmBlock] = useState<Conversation | null>(null);
  const [reportDialog, setReportDialog] = useState<Conversation | null>(null);
  const [reportReason, setReportReason] = useState('');
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Fetch conversations
  const fetchConversations = async (includeArchived: boolean = false, showLoading: boolean = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);
      const response: any = await apiService.get(`/messages/conversations?includeArchived=${includeArchived}`);
      
      if (response.success) {
        setConversations(response.conversations || []);
      } else {
        setError('Failed to load conversations');
      }
    } catch (err: any) {
      console.error('Error fetching conversations:', err);
      setError(err.message || 'Failed to load conversations');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchConversations(activeTab === 'archived');
  }, [activeTab]);

  // Listen for new messages
  useEffect(() => {
    const unsubscribe = onMessageReceive((message) => {
      console.log('📨 New message received in inbox:', message);
      // Refresh conversations to update latest message
      fetchConversations(activeTab === 'archived', false);
    });

    return unsubscribe;
  }, [onMessageReceive, activeTab]);

  // Listen for message status updates
  useEffect(() => {
    const unsubscribe = onMessageStatusUpdate((data) => {
      console.log('✓ Message status updated:', data);
      // Refresh to update status indicators if needed
      fetchConversations(activeTab === 'archived', false);
    });

    return unsubscribe;
  }, [onMessageStatusUpdate, activeTab]);

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: 'inbox' | 'archived') => {
    setActiveTab(newValue);
  };

  // Navigate to chat
  const handleConversationClick = (conversation: Conversation) => {
    navigate(`/inbox/${conversation.partner.id}`, {
      state: {
        contextType: conversation.contextType,
        contextId: conversation.contextId,
        partnerName: `${conversation.partner.preferredName || conversation.partner.firstName || ''} ${conversation.partner.lastName ? conversation.partner.lastName.charAt(0) + '.' : ''}`
      }
    });
  };

  // Open kebab menu
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, conversation: Conversation) => {
    event.stopPropagation();
    setMenuAnchor({ element: event.currentTarget, conversation });
  };

  // Close kebab menu
  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  // Archive conversation
  const handleArchive = async (conversation: Conversation) => {
    try {
      await apiService.post('/message-actions/archive', {
        contextId: conversation.contextId,
        threadPartnerId: conversation.partner.id,
        contextType: conversation.contextType
      });
      
      setConfirmArchive(null);
      handleMenuClose();
      fetchConversations(activeTab === 'archived', false);
      setMessage({ text: 'Conversation archived', type: 'success' });
    } catch (err: any) {
      console.error('Error archiving conversation:', err);
      alert('Failed to archive conversation');
    }
  };

  // Unarchive conversation
  const handleUnarchive = async (conversation: Conversation) => {
    try {
      await apiService.post('/message-actions/unarchive', {
        contextId: conversation.contextId,
        threadPartnerId: conversation.partner.id
      });
      
      handleMenuClose();
      fetchConversations(true, false); // Refresh archived list
      setMessage({ text: 'Conversation moved to inbox', type: 'success' });
    } catch (err: any) {
      console.error('Error unarchiving conversation:', err);
      alert('Failed to unarchive conversation');
    }
  };

  // Delete conversation
  const handleDelete = async (conversation: Conversation) => {
    try {
      await apiService.post('/message-actions/delete-conversation', {
        contextId: conversation.contextId,
        threadPartnerId: conversation.partner.id
      });
      
      setConfirmDelete(null);
      handleMenuClose();
      fetchConversations(activeTab === 'archived', false);
      setMessage({ text: 'Conversation deleted', type: 'success' });
    } catch (err: any) {
      console.error('Error deleting conversation:', err);
      alert('Failed to delete conversation');
    }
  };

  // Mark unread/read
  const handleToggleReadStatus = async (conversation: Conversation) => {
    const isUnread = conversation.unreadCount > 0;
    
    // Optimistic update
    setConversations(prev => prev.map(c => {
      if (c.partner.id === conversation.partner.id && c.contextId === conversation.contextId) {
        return {
          ...c,
          unreadCount: isUnread ? 0 : 1
        };
      }
      return c;
    }));

    try {
      const endpoint = isUnread ? '/message-actions/mark-read' : '/message-actions/mark-unread';
      
      await apiService.post(endpoint, {
        contextId: conversation.contextId,
        threadPartnerId: conversation.partner.id
      });
      
      handleMenuClose();
      // Still fetch fresh data to be sure
      fetchConversations(activeTab === 'archived', false);
      setMessage({ 
        text: `Conversation marked as ${isUnread ? 'read' : 'unread'}`, 
        type: 'success' 
      });
    } catch (err: any) {
      console.error('Error toggling read status:', err);
      // Revert on error
      fetchConversations(activeTab === 'archived', false);
      alert('Failed to update read status');
    }
  };

  // Mute conversation
  const handleMute = async (conversation: Conversation) => {
    // Optimistic update
    setConversations(prev => prev.map(c => {
      if (c.partner.id === conversation.partner.id && c.contextId === conversation.contextId) {
        return { ...c, isMuted: true };
      }
      return c;
    }));

    try {
      await apiService.post('/message-actions/mute', {
        threadPartnerId: conversation.partner.id,
        contextId: conversation.contextId
      });
      
      handleMenuClose();
      setMessage({ text: 'Notifications muted for this conversation', type: 'success' });
    } catch (err: any) {
      console.error('Error muting conversation:', err);
      fetchConversations(activeTab === 'archived', false);
      alert('Failed to mute conversation');
    }
  };

  // Unmute conversation
  const handleUnmute = async (conversation: Conversation) => {
    // Optimistic update
    setConversations(prev => prev.map(c => {
      if (c.partner.id === conversation.partner.id && c.contextId === conversation.contextId) {
        return { ...c, isMuted: false };
      }
      return c;
    }));

    try {
      await apiService.post('/message-actions/unmute', {
        threadPartnerId: conversation.partner.id,
        contextId: conversation.contextId
      });
      
      handleMenuClose();
      setMessage({ text: 'Notifications unmuted for this conversation', type: 'success' });
    } catch (err: any) {
      console.error('Error unmuting conversation:', err);
      fetchConversations(activeTab === 'archived', false);
      alert('Failed to unmute conversation');
    }
  };

  // Block user
  const handleBlock = async (conversation: Conversation) => {
    try {
      await apiService.post('/message-actions/block', {
        threadPartnerId: conversation.partner.id
      });
      
      setConfirmBlock(null);
      handleMenuClose();
      fetchConversations(activeTab === 'archived', false);
      setMessage({ text: 'User blocked successfully', type: 'success' });
    } catch (err: any) {
      console.error('Error blocking user:', err);
      alert('Failed to block user');
    }
  };

  // Unblock user
  const handleUnblock = async (conversation: Conversation) => {
    try {
      await apiService.post('/message-actions/unblock', {
        threadPartnerId: conversation.partner.id
      });
      
      handleMenuClose();
      fetchConversations(activeTab === 'archived', false);
      setMessage({ text: 'User unblocked successfully', type: 'success' });
    } catch (err: any) {
      console.error('Error unblocking user:', err);
      alert('Failed to unblock user');
    }
  };

  // Report conversation
  const handleReport = async (conversation: Conversation) => {
    if (!reportReason || reportReason.length < 10) {
      alert('Please provide a reason with at least 10 characters.');
      return;
    }

    try {
      await apiService.post('/message-actions/report', {
        messageId: conversation.latestMessage.id,
        reason: reportReason
      });
      
      setReportDialog(null);
      setReportReason('');
      handleMenuClose();
      setMessage({ text: 'Conversation reported to admin', type: 'success' });
    } catch (err: any) {
      console.error('Error reporting conversation:', err);
      alert('Failed to report conversation');
    }
  };

  // Get status badge
  const getStatusBadge = (conversation: Conversation) => {
    if (!conversation.contextType || !conversation.userRole) return null;

    let label = '';
    let color: 'primary' | 'secondary' | 'success' | 'warning' = 'primary';

    if (conversation.contextType === 'ride') {
      if (conversation.userRole === 'driver') {
        label = 'YOU OFFERED';
        color = 'primary'; // Blue
      } else {
        label = 'JOINING RIDE';
        color = 'warning'; // Orange
      }
    } else if (conversation.contextType === 'request') {
      if (conversation.userRole === 'passenger') {
        label = 'YOU REQUESTED';
        color = 'success'; // Green
      } else {
        label = 'YOUR BID';
        color = 'secondary'; // Purple
      }
    } else if (conversation.contextType === 'support') {
      label = 'SUPPORT';
      color = 'info' as any;
    }

    return (
      <Chip
        label={label}
        color={color as any}
        size="small"
        sx={{ fontWeight: 'bold', fontSize: '1.05rem', mr: 1 }}
      />
    );
  };

  // Get route text
  const getRouteText = (conversation: Conversation) => {
    const details = conversation.contextDetails;
    if (!details) return null;

    if (conversation.contextType === 'support') {
      return (
        <Typography variant="body2" fontWeight="bold" component="span" sx={{ fontSize: '1.3125rem', color: 'primary.main' }}>
          Support Ticket: {details.subject}
        </Typography>
      );
    }

    if (!details.originCity || !details.destinationCity) {
      return null;
    }

    return (
      <Typography variant="body2" fontWeight="bold" component="span" sx={{ fontSize: '1.3125rem' }}>
        {details.originCity.name} ➔ {details.destinationCity.name}
      </Typography>
    );
  };

  // Get timing text
  const getTimingText = (conversation: Conversation) => {
    const details = conversation.contextDetails;
    if (!details) return null;

    const dateTime = details.departureDateTime || details.preferredDateTime;
    if (!dateTime) return null;

    return (
      <Typography variant="body2" color="text.secondary" component="span" sx={{ fontSize: '1.3125rem' }}>
        {format(new Date(dateTime), 'MMM dd \'at\' h:mm a')}
      </Typography>
    );
  };

  // Render conversation item
  const renderConversation = (conversation: Conversation) => {
    const isPartnerOnline = isOnline(conversation.partner.id);
    const isUnread = conversation.unreadCount > 0;

    return (
      <ListItem
        key={`${conversation.partner.id}-${conversation.contextId || 'general'}`}
        disablePadding
        divider
        sx={{
          bgcolor: conversation.isBlocked ? 'rgba(211, 47, 47, 0.05)' : 'inherit',
          transition: 'background-color 0.2s'
        }}
        secondaryAction={
          <IconButton
            edge="end"
            onClick={(e) => handleMenuOpen(e, conversation)}
          >
            <MoreVertIcon />
          </IconButton>
        }
      >
        <ListItemButton onClick={() => handleConversationClick(conversation)}>
          <ListItemAvatar>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                isPartnerOnline ? (
                  <OnlineDotIcon
                    sx={{
                      width: 12,
                      height: 12,
                      color: 'success.main',
                      bgcolor: 'white',
                      borderRadius: '50%'
                    }}
                  />
                ) : null
              }
            >
              <Avatar
                src={getFullImageUrl(conversation.partner.profilePicture) || undefined}
                alt={conversation.partner.preferredName || conversation.partner.firstName}
                sx={{ width: 56, height: 56, opacity: conversation.isBlocked ? 0.6 : 1 }}
              >
                {(conversation.partner.preferredName || conversation.partner.firstName)[0]}
              </Avatar>
            </Badge>
          </ListItemAvatar>
          
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                {getStatusBadge(conversation)}
                {conversation.isBlocked && (
                  <Chip 
                    label="BLOCKED" 
                    color="error" 
                    size="small" 
                    sx={{ fontWeight: 'bold', fontSize: '1.05rem', mr: 1 }} 
                  />
                )}
                {getRouteText(conversation)}
              </Box>
            }
            secondary={
              <Box sx={{ opacity: conversation.isBlocked ? 0.7 : 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={isUnread ? 'bold' : 'normal'} sx={{ fontSize: '1.3125rem' }}>
                    {conversation.partner.preferredName || conversation.partner.firstName} {conversation.partner.lastName ? conversation.partner.lastName.charAt(0) + '.' : ''}
                  </Typography>
                  {conversation.isMuted && (
                    <MuteIcon sx={{ fontSize: '1.875rem', color: 'error.main' }} />
                  )}
                  {conversation.partner.isDriver && (
                    <VerifiedIcon sx={{ fontSize: '1.5rem', color: 'primary.main' }} />
                  )}
                </Box>
                
                {getTimingText(conversation)}
                
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.5,
                    fontWeight: isUnread ? 'bold' : 'normal',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '1.3125rem',
                    fontStyle: conversation.isBlocked ? 'italic' : 'normal'
                  }}
                >
                  {conversation.isBlocked ? 'This user is blocked' : conversation.latestMessage.content}
                </Typography>
                
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '1.125rem' }}>
                  {formatDistanceToNow(new Date(conversation.latestMessage.createdAt), { addSuffix: true })}
                </Typography>
              </Box>
            }
          />
          
          {isUnread && !conversation.isBlocked && (
            <Badge
              badgeContent={conversation.unreadCount}
              color="primary"
              sx={{ mr: 2 }}
            />
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ fontSize: '2.25rem' }}>
            Chat
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.3125rem' }}>
            Connect with drivers and passengers
          </Typography>
        </Box>

        {/* Alerts */}
        {message && (
          <Alert 
            severity={message.type} 
            onClose={() => setMessage(null)}
            sx={{ m: 2, '& .MuiAlert-message': { fontSize: '1.2rem' } }}
          >
            {message.text}
          </Alert>
        )}

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { fontSize: '1.3125rem' } }}
        >
          <Tab
            value="inbox"
            label="Chat"
            icon={<InboxIcon sx={{ fontSize: '1.5rem' }} />}
            iconPosition="start"
          />
          <Tab
            value="archived"
            label="Archive"
            icon={<ArchiveIcon sx={{ fontSize: '1.5rem' }} />}
            iconPosition="start"
          />
        </Tabs>

        {/* Content */}
        <Box sx={{ minHeight: 400 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          ) : conversations.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <InboxIcon sx={{ fontSize: '4.5rem', color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: '1.875rem' }}>
                No conversations yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.3125rem' }}>
                Start a conversation by messaging a driver or passenger
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {conversations.map((conversation, index) => (
                <React.Fragment key={`${conversation.partner.id}-${conversation.contextId || 'general'}`}>
                  {renderConversation(conversation)}
                  {index < conversations.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Paper>

      {/* Kebab Menu */}
      <Menu
        anchorEl={menuAnchor?.element}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { width: 250 }
        }}
      >
        {activeTab === 'inbox' ? (
          <MenuItem
            onClick={() => {
              if (menuAnchor?.conversation) {
                setConfirmArchive(menuAnchor.conversation);
              }
            }}
            sx={{ fontSize: '1.3125rem', py: 1.5 }}
          >
            <ArchiveIcon sx={{ mr: 2, fontSize: '1.5rem' }} />
            Archive Chat
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              if (menuAnchor?.conversation) {
                handleUnarchive(menuAnchor.conversation);
              }
            }}
            sx={{ fontSize: '1.3125rem', py: 1.5 }}
          >
            <UnarchiveIcon sx={{ mr: 2, fontSize: '1.5rem' }} />
            Move to Inbox
          </MenuItem>
        )}

        <MenuItem
          onClick={() => {
            if (menuAnchor?.conversation) {
              handleToggleReadStatus(menuAnchor.conversation);
            }
          }}
          sx={{ fontSize: '1.3125rem', py: 1.5 }}
        >
          {menuAnchor?.conversation.unreadCount && menuAnchor.conversation.unreadCount > 0 ? (
            <>
              <ReadIcon sx={{ mr: 2, fontSize: '1.5rem' }} />
              Mark as Read
            </>
          ) : (
            <>
              <UnreadIcon sx={{ mr: 2, fontSize: '1.5rem' }} />
              Mark as Unread
            </>
          )}
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (menuAnchor?.conversation) {
              if (menuAnchor.conversation.isMuted) {
                handleUnmute(menuAnchor.conversation);
              } else {
                handleMute(menuAnchor.conversation);
              }
            }
          }}
          sx={{ fontSize: '1.3125rem', py: 1.5 }}
        >
          {menuAnchor?.conversation.isMuted ? (
            <>
              <UnmuteIcon sx={{ mr: 2, fontSize: '1.5rem' }} />
              Unmute Notifications
            </>
          ) : (
            <>
              <MuteIcon sx={{ mr: 2, fontSize: '1.5rem' }} />
              Mute Notifications
            </>
          )}
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (menuAnchor?.conversation) {
              if (menuAnchor.conversation.isBlocked) {
                handleUnblock(menuAnchor.conversation);
              } else {
                setConfirmBlock(menuAnchor.conversation);
              }
            }
          }}
          sx={{ 
            fontSize: '1.3125rem', 
            py: 1.5,
            color: menuAnchor?.conversation.isBlocked ? 'success.main' : 'error.main'
          }}
        >
          {menuAnchor?.conversation.isBlocked ? (
            <>
              <VerifiedIcon sx={{ mr: 2, fontSize: '1.5rem' }} />
              Unblock User
            </>
          ) : (
            <>
              <BlockIcon sx={{ mr: 2, fontSize: '1.5rem' }} />
              Block User
            </>
          )}
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (menuAnchor?.conversation) {
              setReportDialog(menuAnchor.conversation);
              handleMenuClose();
            }
          }}
          sx={{ fontSize: '1.3125rem', py: 1.5, color: 'warning.main' }}
        >
          <ReportIcon sx={{ mr: 2, fontSize: '1.5rem' }} />
          Report to Admin
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={() => {
            if (menuAnchor?.conversation) {
              setConfirmDelete(menuAnchor.conversation);
              handleMenuClose();
            }
          }}
          sx={{ fontSize: '1.3125rem', py: 1.5, color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 2, fontSize: '1.5rem' }} />
          Delete Chat
        </MenuItem>
      </Menu>

      {/* Archive Confirmation Dialog */}
      <Dialog open={Boolean(confirmArchive)} onClose={() => setConfirmArchive(null)}>
        <DialogTitle sx={{ fontSize: '1.875rem' }}>Archive Conversation?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '1.3125rem' }}>
            This conversation will be moved to your archived folder. You can unarchive it anytime.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmArchive(null)} sx={{ fontSize: '1.3125rem' }}>Cancel</Button>
          <Button
            onClick={() => confirmArchive && handleArchive(confirmArchive)}
            color="primary"
            variant="contained"
            sx={{ fontSize: '1.3125rem' }}
          >
            Archive
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)}>
        <DialogTitle sx={{ fontSize: '1.875rem', color: 'error.main' }}>Delete Conversation?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '1.3125rem' }}>
            Are you sure you want to delete this conversation? This will remove it from your list. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmDelete(null)} sx={{ fontSize: '1.3125rem' }}>Cancel</Button>
          <Button
            onClick={() => confirmDelete && handleDelete(confirmDelete)}
            color="error"
            variant="contained"
            sx={{ fontSize: '1.3125rem' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Block Confirmation Dialog */}
      <Dialog open={Boolean(confirmBlock)} onClose={() => setConfirmBlock(null)}>
        <DialogTitle sx={{ fontSize: '1.875rem', color: 'error.main' }}>Block User?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '1.3125rem' }}>
            Are you sure you want to block this user? You will no longer receive messages from them.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmBlock(null)} sx={{ fontSize: '1.3125rem' }}>Cancel</Button>
          <Button
            onClick={() => confirmBlock && handleBlock(confirmBlock)}
            color="error"
            variant="contained"
            sx={{ fontSize: '1.3125rem' }}
          >
            Block
          </Button>
        </DialogActions>
      </Dialog>

      {/* Report Dialog */}
      <Dialog open={Boolean(reportDialog)} onClose={() => setReportDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1.875rem' }}>Report Conversation</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '1.3125rem', mb: 2 }}>
            Please explain why you are reporting this conversation. Our admin team will investigate.
          </Typography>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            placeholder="Reason for reporting (minimum 10 characters)..."
            value={reportReason}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReportReason(e.target.value)}
            sx={{ '& .MuiInputBase-root': { fontSize: '1.3125rem' } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setReportDialog(null)} sx={{ fontSize: '1.3125rem' }}>Cancel</Button>
          <Button
            onClick={() => reportDialog && handleReport(reportDialog)}
            color="warning"
            variant="contained"
            disabled={reportReason.length < 10}
            sx={{ fontSize: '1.3125rem' }}
          >
            Submit Report
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InboxPage;

